import React, { useState, useCallback, useMemo, useEffect, ReactNode } from 'react';
import { SearchFilter } from './SearchFilter';
import { SearchAdvancedFilter } from './SearchAdvancedFilter';
import { SelectFilter } from './SelectFilter';
import { SelectMultiFilter } from './SelectMultiFilter';
import { SelectNativeFilter } from './SelectNativeFilter';
import { DateFilter } from './DateFilter';
import { DateRangeFilter } from './DateRangeFilter';
import { AmountRangeFilter } from './AmountRangeFilter';
import { FilterLayout, FilterMapEntry } from './types';
// import { PaymentTicketStatusesFilter } from './PaymentTicketStatusesFilter';
// import { PaymentMethodsFilter } from './PaymentMethodsFilter';
// import { MerchantsFilter } from './MerchantsFilter';
// import { DateRangeFilter } from './DateRangeFilter';
// import { SearchFilter } from './SearchFilter';

/** Набор базовых фильтров */
export interface IBaseFilters {
    statuses?: string[];
    methods?: string[];
    merchants?: string[];
    dateRange?: { from?: Date; to?: Date; mode?: string };
    amountRange?: { from?: string; to?: string };



    byCompletionDate?: boolean // Переключатель диапазона дат (true - по дате завершения, false - по дате создания)
    dateStart?: Date // Дата начала диапазона
    dateEnd?: Date // Дата конца диапазона


    euConfirmed?: boolean
    perPage?: number
    status?: string[]
    paymentAdapterId?: string // адаптер, в рамках которого ищется ID операции (поиск по ID адаптера)

    search?: string // поисмк по конкретному полю
    searchAdvanced?: { field?: string; query: string } // мультипоиск по полям (id тикета + id транзакции + токен тикета)
    select?: string // универсальный фильтр-селект (themed react-select, single)
    selectMulti?: string[] // мульти react-select с темой
    selectNative?: string // нативный <select> вариант
    date?: Date | null // single date picker
}

type FilterComponentMap<T extends IBaseFilters> = {
    [K in keyof T]?: FilterMapEntry<T[K]>;
};

interface ActiveFilterItem<T> {
    /** Фильтр */
    filter: keyof T;
    /** Альтернативный ключь для фильтра */
    fieldName?: string;
    /** Доступность отображения. Если true то фильтр отобразится, иначе будет скрыт */
    visibility?: boolean
}

type ActiveFilter<T extends IBaseFilters = IBaseFilters> =
    | keyof T
    | ActiveFilterItem<T>;

/**
 * Универсальный менеджер фильтров.
 *
 * @template T Тип объекта фильтров, должен наследоваться от IBaseFilters.
 */
interface IFilterManagerProps<T extends IBaseFilters> {
    /**
    * Список активных фильтров.
    * 
    * Пример: 
    * ```ts
    * ['search', { filter: 'byCompletionDate', visibility: visibility.token, fieldName: 'searchMerchant' }]
    * ```
    */
    activeFilters: ActiveFilter<T>[];

    /** Значения фильтров по умолчанию. */
    defaultValues?: Partial<T>;

    /**
     * Колбэк, вызываемый при применении фильтров.
     *
     * @param filters Объект фильтров, где ключи соответствуют типу T.
     */
    onApplyFilters: (filters: T) => void;

    /** Можно добавить собственные фильтры */
    customFilters?: FilterComponentMap<T>;

    /**
     * Дополнительные кнопки-действия страницы (например «Экспортировать»).
     * Рендерятся в той же колонке под «Применить» / «Сбросить», чтобы кнопки страницы
     * не разъезжались по шапке.
     */
    actions?: ReactNode;

    /** Дополнительные параметры для  переопределения в компонентах фильтрации  */
    filterProps?: Partial<Record<keyof T, Record<string, any>>>;
}

/**
 * Universal filter manager for admin pages.
 *
 * Renders a card with two halves: the left (`col-lg-9`) hosts a responsive
 * grid of active filters, the right (`col-lg-3`) hosts «Применить» / «Сбросить»
 * buttons.
 *
 * Active filters are listed via {@link IFilterManagerProps.activeFilters}. Each
 * entry resolves into a component from `baseFilterMap` (or `customFilters`),
 * and its rendered output is wrapped according to the entry's `layout`:
 *   - `fullWidth` → `col-12` (search, searchAdvanced, dateRange)
 *   - `grid`      → `col-12 col-md-6 col-lg-4 col-xl-3` (select, selectMulti, selectNative, date)
 *   - `raw`       → no wrapper. Default for `customFilters`; the consumer controls layout.
 *
 * Supported base filter keys:
 *
 * | key              | value                                              | notes                              |
 * |------------------|----------------------------------------------------|------------------------------------|
 * | `search`         | `string`                                           | full-width text input              |
 * | `searchAdvanced` | `{ field: string; query: string }`                 | text + field selector overlay      |
 * | `select`         | `string`                                           | themed react-select (single)       |
 * | `selectMulti`    | `string[]`                                         | themed react-select (multi)        |
 * | `selectNative`   | `string`                                           | native `<select>` fallback         |
 * | `date`           | `Date \| null`                                     | single date picker                 |
 * | `dateRange`      | `{ from?: Date; to?: Date; mode?: string }`        | range + optional modes + buttons   |
 * | `amountRange`    | `{ from?: string; to?: string }`                   | amount bounds, digits only         |
 *
 * The {@link ActiveFilterItem.fieldName} mechanism lets one filter type back
 * several state keys (e.g. three `select`s with different `fieldName`s).
 * See `frontend/docs/filter-manager.md` for the full guide.
 *
 * @example minimal
 * ```tsx
 * <FilterManager
 *   activeFilters={['searchAdvanced']}
 *   defaultValues={{ searchAdvanced: { field: 'id', query: '' } }}
 *   filterProps={{
 *     searchAdvanced: { options: [{value: 'id', label: 'По ID'}] }
 *   }}
 *   onApplyFilters={(f) => console.log(f.searchAdvanced)}
 * />
 * ```
 *
 * @example mixed selects + custom filter
 * ```tsx
 * <FilterManager<MyFilters>
 *   activeFilters={[
 *     { filter: 'select',      fieldName: 'direction' },
 *     { filter: 'selectMulti', fieldName: 'statuses' },
 *     'merchantToken',
 *   ]}
 *   filterProps={{
 *     direction: { label: 'Направление', options: directionOptions },
 *     statuses:  { label: 'Статусы',     options: statusOptions },
 *   }}
 *   customFilters={{
 *     merchantToken: { component: MerchantAdapter, defaultValue: '', layout: 'grid' },
 *   }}
 *   onApplyFilters={apply}
 * />
 * ```
 */
export function FilterManager<T extends IBaseFilters = IBaseFilters>({
    activeFilters,
    defaultValues,
    onApplyFilters,
    customFilters,
    actions,
    filterProps,
}: IFilterManagerProps<T>) {
    const createFilterMap = <T extends IBaseFilters>(map: FilterComponentMap<T>): FilterComponentMap<T> => { return map; }

    // Карта доступных фильтров
    const baseFilterMap = createFilterMap<T>({
        search:         { component: SearchFilter as any,         defaultValue: '' as any,                              layout: 'fullWidth' },
        searchAdvanced: { component: SearchAdvancedFilter as any, defaultValue: { field: 'id', query: '' } as any,     layout: 'fullWidth' },
        select:         { component: SelectFilter as any,         defaultValue: '' as any,                              layout: 'grid' },
        selectMulti:    { component: SelectMultiFilter as any,    defaultValue: [] as any,                              layout: 'grid' },
        selectNative:   { component: SelectNativeFilter as any,   defaultValue: '' as any,                              layout: 'grid' },
        date:           { component: DateFilter as any,           defaultValue: null as any,                            layout: 'grid' },
        dateRange:      { component: DateRangeFilter as any,      defaultValue: {} as any,                              layout: 'fullWidth' },
        amountRange:    { component: AmountRangeFilter as any,    defaultValue: {} as any,                              layout: 'grid' },
    });

    const mergedMap: FilterComponentMap<T> = { ...baseFilterMap, ...customFilters };

    const [filtersState, setFiltersState] = useState<T>(initFiltersState<T>(activeFilters, defaultValues, mergedMap))

    // Нужно или пробрасывать состояние из родителя или отказаться
    // ТАк же нужно оставить если планируется динамическая смена фильтров в будущем
    // useEffect(() => {
    //     setFiltersState(initFiltersState<T>(activeFilters, defaultValues, mergedMap));
    // }, [activeFilters]);

    function isFilterItem<T extends IBaseFilters>(
        value: ActiveFilter<T>
    ): value is ActiveFilterItem<T> {
        return typeof value === 'object' && value !== null && 'filter' in value;
    }

    // Пересобираем значение фильтрорв
    function initFiltersState<T extends IBaseFilters>(
        activeFilters: ActiveFilter<T>[],
        defaultValues: Partial<T> | undefined,
        filterMap: FilterComponentMap<T>
    ): T {
        const state = { ...(defaultValues ?? {}) } as Partial<T>;

        for (const f of activeFilters) {
            const filterKey = isFilterItem(f) ? f.filter : f;
            const storageKey = isFilterItem(f) ? (f.fieldName ?? f.filter) : f;

            if (
                isFilterItem(f) &&
                f.fieldName &&
                defaultValues &&
                filterKey in defaultValues &&
                !(f.fieldName in state)
            ) {
                (state as Record<string, any>)[f.fieldName as string] =
                    (defaultValues as Record<string, any>)[filterKey as string];
                delete (state as Record<string, any>)[filterKey as string];
                continue;
            }

            if (!Object.prototype.hasOwnProperty.call(state, storageKey)) {
                const def = filterMap[filterKey as keyof T]?.defaultValue;
                (state as Record<keyof T, any>)[storageKey as keyof T] = def ?? null;
            }

        }

        return state as T;
    }

    // Обновление значения фильтра
    const updateFilter = useCallback(
        <K extends keyof T>(name: K, value: T[K]) => {
            setFiltersState((prev) => ({ ...prev, [name]: value }));
        },
        []
    );

    const colClass: Record<FilterLayout, string> = {
        fullWidth: 'col-12',
        grid:      'col-12 col-md-6 col-lg-4 col-xl-3',
        raw:       '',
    }

    // Вычисляем фильтры, которые можно показать
    const visibleFilters = useMemo(() => {
        return activeFilters
            .filter((f) => {
                if (!isFilterItem(f)) return true;
                if (f.visibility === false) return false;
                return true;
            })
            .map((f) => {
                const filterKey = isFilterItem(f) ? f.filter : f;
                const storageKey = isFilterItem(f) && f.fieldName ? f.fieldName : filterKey

                const entry = mergedMap[filterKey as keyof T];
                if (!entry) return null;
                const Comp = entry.component;
                const layout: FilterLayout = entry.layout ?? 'raw';

                const extraProps = filterProps?.[storageKey as keyof T] ?? filterProps?.[filterKey as keyof T] ?? {};

                const key = `filter_${String(storageKey)}`;
                const filterEl = (
                    <Comp
                        value={filtersState[storageKey as keyof T]}
                        onChange={(v: any) => updateFilter(storageKey as keyof T, v)}
                        {...extraProps}
                    />
                );

                if (layout === 'raw') {
                    return React.cloneElement(filterEl, { key });
                }
                return (
                    <div key={key} className={colClass[layout]}>
                        {filterEl}
                    </div>
                );
            })
            .filter(Boolean);
    }, [activeFilters, filtersState, updateFilter, mergedMap, filterProps]);


    const handleApply = useCallback(() => {
        onApplyFilters(filtersState);
    }, [filtersState, onApplyFilters]);

    // Без useCallback: значения по умолчанию собираются заново на каждом рендере, мемоизировать нечего.
    const handleReset = () => {
        // Не только defaultValues: фильтр без явного значения по умолчанию получает своё базовое
        // значение, как при монтировании, — иначе компонент получает undefined.
        const resetValues = initFiltersState<T>(activeFilters, defaultValues, mergedMap);
        setFiltersState(resetValues);
        onApplyFilters(resetValues);
    };

    const isFilterFormVisible = useMemo(() => {
        return visibleFilters.length > 0;
    }, [visibleFilters]);

    if (!isFilterFormVisible) return null;

    return (
        <div className="card mb-7">
            <div className="card-body border-0 pt-5">
                <div className='row'>
                    <div className='col-lg-9'>
                        <div className='row g-3'>{visibleFilters}</div>
                    </div>
                    <div className='d-flex flex-column col-lg-3 gap-3 mt-3 mt-lg-0'>
                        <button className='btn btn-success' onClick={handleApply}>
                            Применить
                        </button>
                        <button className='btn btn-primary' onClick={handleReset}>
                            Сбросить&nbsp;фильтр
                        </button>
                        {actions}
                    </div>
                </div>
            </div>
        </div>
    );
}
